import { useQuery, useMutation } from "./urqlHooks";
import { makeOperation } from "@urql/core";
import jwt_decode from "jwt-decode";
import {
  COOKIE_SESSION_SENTINEL,
  hasClientSession,
} from "../utils/session";

const AUTH_PAYLOAD_FIELDS = `
    accessToken
    sessionEstablished
    sessionUser {
      id
      firstName
      lastName
      email
      avatar
      roles
      exp
    }
    mfaSetupToken
    otpauthUri
    mfaSecret
    mfaChallengeToken
    recoveryCodes
    mfaMethod
    mfaAvailableMethods
    mfaEmailMasked
`;

const LOG_IN = `
mutation login(
  $email: String!
  $challengeId: String
  $encryptedAesKey: String
  $encryptedPayload: String
  $pass: String
) {
  login(
    input: {
      email: $email
      challengeId: $challengeId
      encryptedAesKey: $encryptedAesKey
      encryptedPayload: $encryptedPayload
      password: $pass
    }
  ) {
${AUTH_PAYLOAD_FIELDS}
  }
}
`;

export const LogIn = () => {
  const [_updateLogIn, logIn] = useMutation(LOG_IN);
  return logIn;
};

const CREATE_LOGIN_CHALLENGE = `
query createLoginChallenge {
  createLoginChallenge {
    challengeId
    nonce
    expiresInSec
  }
}
`;

export const CreateLoginChallenge = () => {
  const [result, reexecuteQuery] = useQuery({
    query: CREATE_LOGIN_CHALLENGE,
    pause: true,
    requestPolicy: "network-only",
  });
  return { ...result, reexecuteQuery };
};

const MFA_ENROLL_BEGIN = `
mutation mfaEnrollBegin($setupToken:String!,$method:MfaMethod!){
  mfaEnrollBegin(setupToken:$setupToken,method:$method){
    mfaSetupToken
    otpauthUri
    mfaSecret
    mfaMethod
    mfaEmailMasked
    mfaAvailableMethods
  }
}
`;

export const MfaEnrollBegin = () => {
  const [, mutate] = useMutation(MFA_ENROLL_BEGIN);
  return mutate;
};

const MFA_ENROLL_CONFIRM = `
mutation mfaEnrollConfirm($setupToken:String!,$code:String!,$method:MfaMethod){
  mfaEnrollConfirm(setupToken:$setupToken,code:$code,method:$method){
${AUTH_PAYLOAD_FIELDS}
  }
}
`;

export const MfaEnrollConfirm = () => {
  const [, mutate] = useMutation(MFA_ENROLL_CONFIRM);
  return mutate;
};

const MFA_VERIFY = `
mutation mfaVerify($challengeToken:String!,$code:String,$recoveryCode:String){
  mfaVerify(challengeToken:$challengeToken,code:$code,recoveryCode:$recoveryCode){
${AUTH_PAYLOAD_FIELDS}
  }
}
`;

export const MfaVerify = () => {
  const [, mutate] = useMutation(MFA_VERIFY);
  return mutate;
};

const MFA_RESEND_EMAIL_OTP = `
mutation mfaResendEmailOtp($token:String!){
  mfaResendEmailOtp(token:$token){
    mfaMethod
    mfaEmailMasked
  }
}
`;

export const MfaResendEmailOtp = () => {
  const [, mutate] = useMutation(MFA_RESEND_EMAIL_OTP);
  return mutate;
};

const LOGOUT_MUTATION = `
mutation logout {
  logout
}
`;

export const LogoutMutation = () => {
  const [, logout] = useMutation(LOGOUT_MUTATION);
  return logout;
};

const SIGN_IN = LOG_IN;

export const SignIn = () => {
  const [result, reexecuteMutation] = useMutation(SIGN_IN);
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteMutation };
};

/**
 * Login bootstrap + MFA: no usan el JWT de sesión. Incluye createLoginChallenge
 * (query): si queda authState zombie tras idle, willAuthError no debe cortar el
 * flujo antes de login/sendEmailOtp.
 *
 * No incluir `logout`: addAuthToOperation debe poder enviar cookie/Authorization.
 */
const AUTH_BOOTSTRAP_FREE_OPERATIONS = new Set([
  'createLoginChallenge',
  'login',
  'mfaEnrollBegin',
  'mfaEnrollConfirm',
  'mfaVerify',
  'mfaResendEmailOtp',
]);

const operationHasBootstrapFreeField = (operation) =>
  (operation?.query?.definitions || []).some(
    (definition) =>
      definition.kind === 'OperationDefinition' &&
      (definition.selectionSet?.selections || []).some(
        (node) =>
          node.kind === 'Field' &&
          AUTH_BOOTSTRAP_FREE_OPERATIONS.has(node.name?.value),
      ),
  );

/** Covers login bootstrap + MFA free ops (name kept for existing specs). */
export const isMfaFreeAccessOperation = (operation) => {
  if (operation?.kind !== 'mutation' && operation?.kind !== 'query') {
    return false;
  }
  return operationHasBootstrapFreeField(operation);
};

const clearClientSessionMarkers = () => {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('logOut');
  } catch (_) {
    /* ignore */
  }
};

export const isMfaSessionExpiredError = (err) => {
  const message = err?.graphQLErrors?.[0]?.message || err?.message || '';
  return /expir[oó]|vuelve a iniciar sesión/i.test(String(message));
};

export const getAuth = async ({ authState }) => {
  const legacyToken = localStorage.getItem("token");

  // Init: cookie session (preferred) or legacy JWT in localStorage.
  if (!authState) {
    if (legacyToken) {
      return { token: legacyToken, refreshToken: legacyToken };
    }
    if (hasClientSession()) {
      return {
        token: COOKIE_SESSION_SENTINEL,
        refreshToken: COOKIE_SESSION_SENTINEL,
      };
    }
    return null;
  }

  if (legacyToken && legacyToken !== authState.token) {
    return { token: legacyToken, refreshToken: legacyToken };
  }

  if (
    !legacyToken &&
    hasClientSession() &&
    authState.token !== COOKIE_SESSION_SENTINEL
  ) {
    return {
      token: COOKIE_SESSION_SENTINEL,
      refreshToken: COOKIE_SESSION_SENTINEL,
    };
  }

  // Auth error and no recoverable session → logout.
  // Content's useCheckSession only reads logOut on render, so navigate here.
  const onSignIn =
    typeof window !== "undefined" &&
    (window.location?.pathname === "/signin" ||
      window.location?.hash?.includes("signin"));

  if (onSignIn) {
    // Ya en login: limpiar marcadores para no recrear sentinel zombie en el
    // próximo getAuth(null). No solo setItem(logOut).
    clearClientSessionMarkers();
    return null;
  }

  try {
    localStorage.setItem("logOut", true);
  } catch (_) {
    /* ignore */
  }
  if (typeof window !== "undefined") {
    window.location.assign("/signin");
  }
  return null;
};

export const addAuthToOperation = ({ authState, operation }) => {
  if (isMfaFreeAccessOperation(operation)) {
    return operation;
  }
  if (!authState || !authState.token) {
    return operation;
  }
  const fetchOptions =
    typeof operation.context.fetchOptions === "function"
      ? operation.context.fetchOptions()
      : operation.context.fetchOptions || {};

  const headers = { ...fetchOptions.headers };
  const legacyToken =
    authState?.token && authState.token !== COOKIE_SESSION_SENTINEL
      ? authState.token
      : null;

  if (legacyToken) {
    try {
      jwt_decode(legacyToken);
    } catch {
      /* ignore decode for header attach */
    }
    headers.Authorization = "Bearer " + legacyToken;
  }

  return makeOperation(operation.kind, operation, {
    ...operation.context,
    fetchOptions: {
      ...fetchOptions,
      credentials: "include",
      headers,
    },
  });
};

const authErrorStatus = (err) => {
  const ext = err?.extensions || {};
  const raw = ext.statusCode ?? ext.code ?? ext.exception?.status;
  const n = Number(raw);
  return Number.isFinite(n) ? n : raw;
};

export const didAuthError = ({ error }) => {
  const hasForbiddenError = (error.graphQLErrors || []).some((e) => {
    const code = e.extensions?.code;
    const status = authErrorStatus(e);
    const message = e.message || '';
    if (/expir[oó]|vuelve a iniciar sesión/i.test(message)) {
      return false;
    }
    const errorCode = e.extensions?.originalError?.code || '';
    
    if (errorCode === 'COMPANY_BLOCKED' || errorCode === 'COMPANY_PAYMENT_PENDING') {
      console.warn('[Auth] Error de empresa bloqueada/sin pagar - NO cerrar sesión:', message);
      return false;
    }
    
    if (message.includes('pagos pendientes') || 
        message.includes('ya no es cliente') ||
        message.includes('empresa está bloqueada') ||
        message.includes('empresa est? bloqueada')) {
      console.warn('[Auth] Error de acceso a empresa - NO cerrar sesión:', message);
      return false;
    }
    
    if ((status === 403 || code === 403 || code === "FORBIDDEN" || message.includes('desactivada')) && 
        (message.includes('cuenta') || message.includes('usuario'))) {
      localStorage.removeItem('token');
      localStorage.setItem('logOut', 'true');
      localStorage.setItem('logoutReason', 'deactivated');
      
      setTimeout(() => {
        window.location.href = '/#/signin';
        window.location.reload();
      }, 100);
      
      return true;
    }

    // Nest formatError sets extensions.code to a number (401), not "UNAUTHENTICATED".
    if (
      status === 401 ||
      code === 401 ||
      code === 'UNAUTHENTICATED' ||
      /token de autenticaci[oó]n/i.test(message)
    ) {
      return true;
    }
    
    return code === "FORBIDDEN" && /autenticaci[oó]n/i.test(message);
  });
  
  return hasForbiddenError;
};

/**
 * Operaciones públicas: deben poder ejecutarse sin sesión (registro,
 * recuperación de contraseña, verificación de email, logout).
 *
 * Va en un set aparte de AUTH_BOOTSTRAP_FREE_OPERATIONS a propósito: ese set
 * también gobierna addAuthToOperation, y sacarle el header Authorization a
 * `logout` impediría invalidar la sesión en el server. Acá solo evita que urql
 * marque authError cuando todavía no hay authState.
 *
 * createLoginChallenge vive en AUTH_BOOTSTRAP_FREE_OPERATIONS (willAuthError
 * también con authState zombie).
 */
const PUBLIC_ACCESS_OPERATIONS = new Set([
  'logout',
  'signUp',
  'emailExist',
  'isTokenValid',
  'changePasswordFromReset',
]);

const isPublicAccessOperation = (operation) =>
  operation.query?.definitions?.some(
    (definition) =>
      definition.kind === 'OperationDefinition' &&
      definition.selectionSet?.selections?.some(
        (node) =>
          node.kind === 'Field' && PUBLIC_ACCESS_OPERATIONS.has(node.name?.value),
      ),
  );

export const willAuthError = ({ operation, authState }) => {
  // Login bootstrap / MFA no usan el JWT de sesión. Si quedó un token caducado
  // en memoria, urql no debe intentar refrescar auth ni marcar logout.
  if (isMfaFreeAccessOperation(operation)) {
    return false;
  }

  if (!authState) {
    // Sin sesión, las operaciones públicas son legítimas: marcarlas como
    // authError manda al usuario a login en medio del registro o del reset.
    return !isPublicAccessOperation(operation);
  }

  // Sesión por cookie HttpOnly: el "token" es un centinela, no un JWT. Decodificarlo
  // tira excepción y marcaría authError en cada request.
  if (authState.token && authState.token !== COOKIE_SESSION_SENTINEL) {
    try {
      const { exp } = jwt_decode(authState.token);
      return Number(exp) < Date.now() / 1000;
    } catch {
      return true;
    }
  }

  // Sesión por cookie: no hay JWT que evaluar y la sesión es válida.
  return false;
};

const CHECKEMAIL = `query CHECKEMAIL($email: String!){
  emailExist(email:$email)
}`;
export const CheckEmail = (email) => {
  const [result, executeQuery] = useQuery({
    query: CHECKEMAIL,
    variables: { email },
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, executeQuery, result };
};
const CHANGE_PASSWORD = `mutation CHANGE_PASSWORD(
    $input: ChangePasswordFromResetInput!
  ) {
  changePasswordFromReset(input : $input)
}
`;

export const ChangePassword = () => {
  const [_updateLogIn, changePassword] = useMutation(CHANGE_PASSWORD);
  return changePassword;
};

const IS_TOKEN_VALID = `query IS_TOKEN_VALID($token: String!) {
  isTokenValid(token: $token)
}
`;

export const IsTokenValid = (token) => {
  const [result, reexecuteQuery] = useQuery({
    query: IS_TOKEN_VALID,
    variables: { token },
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const IS_ONBOARDING_TOKEN_VALID = `query IS_ONBOARDING_TOKEN_VALID($userId: String!, $token: String!) {
  isOnboardingTokenValid(userId: $userId, token: $token)
}
`;

export const IsOnboardingTokenValid = (userId, token) => {
  const [result, reexecuteQuery] = useQuery({
    query: IS_ONBOARDING_TOKEN_VALID,
    variables: { userId: userId || '', token: token || '' },
    pause: !userId || !token,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};
